/**
 * ACTIVITY-CORE — human-readable feed text (i18n) for Journal.
 *
 * App-SPECIFIC part: the entity_type+action → phrase map and the
 * labels for patch fields. The structure (parsing, grouping by
 * batch, offline detection) lives in @journal/shared.
 */
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { type ActivityRow, parseActivityPatch } from "@journal/shared";

interface ActivitySentence {
  named: MessageDescriptor;
  anonymous: MessageDescriptor;
}

/**
 * Fine-grained semantic key. Refines `deleted`: a `deleted_at` transition
 * (trash) is "trashed"; a complete snapshot without `deleted_at` is the permanent
 * purge ("purged").
 */
export function activitySemanticKey(row: ActivityRow): string {
  const action = row.action ?? "updated";
  const patch = parseActivityPatch(row.patch);
  if (action === "deleted") {
    return patch.deleted_at ? "trashed" : "purged";
  }
  return action;
}

const ENTRY_TEXTS: Record<string, ActivitySentence> = {
  created: { named: msg`wrote the entry "{name}"`, anonymous: msg`wrote a journal entry` },
  updated: { named: msg`edited the entry "{name}"`, anonymous: msg`edited a journal entry` },
  trashed: {
    named: msg`moved the entry "{name}" to the trash`,
    anonymous: msg`moved an entry to the trash`,
  },
  purged: {
    named: msg`permanently deleted the entry "{name}"`,
    anonymous: msg`permanently deleted an entry`,
  },
  restored: { named: msg`restored the entry "{name}"`, anonymous: msg`restored an entry` },
  reverted: {
    named: msg`undid a change on the entry "{name}"`,
    anonymous: msg`undid a change on an entry`,
  },
};

const TAG_TEXTS: Record<string, ActivitySentence> = {
  created: { named: msg`created the tag "{name}"`, anonymous: msg`created a tag` },
  updated: { named: msg`edited the tag "{name}"`, anonymous: msg`edited a tag` },
  // journal_tag is a hard-delete (no trash): `deleted` = permanent deletion.
  trashed: { named: msg`deleted the tag "{name}"`, anonymous: msg`deleted a tag` },
  purged: { named: msg`deleted the tag "{name}"`, anonymous: msg`deleted a tag` },
  restored: { named: msg`recreated the tag "{name}"`, anonymous: msg`recreated a tag` },
  reverted: {
    named: msg`undid a change on the tag "{name}"`,
    anonymous: msg`undid a change on a tag`,
  },
};

const FALLBACK: ActivitySentence = {
  named: msg`changed "{name}"`,
  anonymous: msg`made a change`,
};

/** Visible entity name extracted from the patch (title/name, new or old). */
export function activityEntityName(row: ActivityRow): string | null {
  const patch = parseActivityPatch(row.patch);
  for (const key of ["title", "name"] as const) {
    const change = patch[key];
    if (!change) {
      continue;
    }
    const value = change.new ?? change.old;
    if (typeof value === "string" && value.trim().length > 0) {
      return value;
    }
  }
  return null;
}

export function activitySentence(row: ActivityRow): {
  descriptor: MessageDescriptor;
  values: Record<string, string>;
} {
  const key = activitySemanticKey(row);
  const table =
    row.entity_type === "journal_entry"
      ? ENTRY_TEXTS
      : row.entity_type === "journal_tag"
        ? TAG_TEXTS
        : {};
  const sentence = table[key] ?? FALLBACK;
  const name = activityEntityName(row);
  if (name) {
    return { descriptor: sentence.named, values: { name } };
  }
  return { descriptor: sentence.anonymous, values: {} };
}

/** i18n label for a patch field (for the old→new diff). */
export function fieldLabel(field: string): MessageDescriptor {
  const map: Record<string, MessageDescriptor> = {
    title: msg`Title`,
    body: msg`Text`,
    mood: msg`Mood`,
    mood_tags: msg`Mood tags`,
    is_favorite: msg`Favorite`,
    word_count: msg`Words`,
    entry_date: msg`Date`,
    template_key: msg`Template`,
    deleted_at: msg`Trash`,
    name: msg`Name`,
    color: msg`Color`,
  };
  return map[field] ?? msg`Field`;
}

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const WEEK_DAYS = 7;

/** "5 min ago" localized with Intl; falls back to the date for older items. */
export function formatRelativeTime(
  iso: string | null,
  locale: string,
  now: Date = new Date(),
): string {
  if (!iso) {
    return "";
  }
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) {
    return "";
  }
  const diff = ts - now.getTime();
  const abs = Math.abs(diff);
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    if (abs < MINUTE_MS) {
      return rtf.format(Math.round(diff / SECOND_MS), "second");
    }
    if (abs < HOUR_MS) {
      return rtf.format(Math.round(diff / MINUTE_MS), "minute");
    }
    if (abs < DAY_MS) {
      return rtf.format(Math.round(diff / HOUR_MS), "hour");
    }
    if (abs < WEEK_DAYS * DAY_MS) {
      return rtf.format(Math.round(diff / DAY_MS), "day");
    }
    return new Date(ts).toLocaleDateString(locale);
  } catch {
    return new Date(ts).toLocaleString();
  }
}
